import React, { useEffect, useState, useRef } from 'react';
import { View, ScrollView, StyleSheet } from 'react-native';
import { getSessionLockService } from '../../../../api/sessionLockService';
import { DLOLaborOptimizationBreadcrumb } from '../../components/DLOBreadcrumb';
import DLOCreateScheduleHeader from '../../components/DLOCreateScheduleHeader';
import DLONotification, { NotificationVariant } from '../../components/DLONotification';
import DLOPrintModal from '../../components/DLOPrintModal';
import DLOExportModal from '../../components/DLOExportModal';
import { useCreateSchedule } from './hooks/useCreateSchedule';
import { useCreateScheduleHandlers } from './hooks/useCreateScheduleHandlers';
import { useGlobalDropdown } from './hooks/useGlobalDropdown';
import { useModalState } from './hooks/useModalState';
import { useTabState } from './hooks/useTabState';
import DLOCreateScheduleTable from './components/DLOCreateScheduleTable';
import DLOCreateScheduleModals from './components/DLOCreateScheduleModals';
import { hasIncompleteEmployeeRows } from '../../components/Tables/DLOCreateScheduleEmployeeRow';
import { getEmployeeOptions, getRoleOptions, getShiftOptions, getMergedRolesWithFilterState, transformApiDataToTableFormat, transformActualHoursToTableFormat, extractRolesByType, getPeriodWeekYearDisplay, compareWeek } from './utils/scheduleHelpers';
import { calculateCanSplitRole } from './utils/handlerHelpers';
import { DLOCreateScheduleProps } from './types';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { fromBackendWeek } from '../../utils/weekTransforms';
import { setFilteredRoles, setActualFilteredRoles } from '../../../../store/slices/dloSlice';
import { fetchKCPHData, fetchCreateScheduleHours, fetchActualHours } from '../../../../store/thunks/dloThunks';
import { useAppDispatch } from '../../../../store';
import { useAutoExtendLock } from '../../hooks/useAutoExtendLock';
import { showSessionLockConflictModal } from '../../../../store/slices/sessionLockSlice';

export default function DLOCreateSchedule({ onNavigate }: DLOCreateScheduleProps) {
  // Auto-extend session lock while user is actively working
  useAutoExtendLock();

  // Custom hooks
  const createScheduleData = useCreateSchedule();
  const { globalOpenDropdown, handleGlobalDropdownOpen, isDropdownOpen } = useGlobalDropdown();
  const { modalState, setModalState } = useModalState();

  // Export and Print modal states
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [printModalVisible, setPrintModalVisible] = useState(false);
  const [hideHours, setHideHours] = useState(false);

  // Gate: once the user makes any change, require an explicit Save before Submit is allowed.
  // Only clears when saveSuccess fires so it's immune to Redux hasUnsavedChanges resets.
  const [requiresSave, setRequiresSave] = useState(false);

  // Function to close all open dropdowns
  const handleCloseAllDropdowns = () => {
    // Reset the global dropdown state by setting it to null
    // This will close all open dropdowns across all rows
    handleGlobalDropdownOpen('', 'employee', false);
  };

  // Extract data and actions from hooks (must be before any usage)
  const {
    scheduleHours,
    scheduleHoursLoading,
    actualHours,
    actualHoursLoading,
    employeeRows,
    actualEmployeeRows,
    employees,
    employeeRoles,
    shiftTimes,
    selectedDays,
    actualSelectedDays,
    dayFilter,
    actualDayFilter,
    filteredRoles,
    actualFilteredRoles,
    scheduleEmployeeDataLoading,
    saveLoading,
    saveSuccess,
    hasUnsavedChanges,
    createSchedulePeriod,
    createScheduleWeek,
    createScheduleYear,
    appData,
    scheduleEmployeeData,
    initialSchedules,
    dispatch,
    resetSaveState,
    updateEmployeeRow,
    updateActualEmployeeRow,
    submitLoading,
    resetCreateScheduleState
  } = createScheduleData;

  const selectedLocationId = useSelector((state: RootState) => state.appData?.selectedLocation?.id);
  const selectedLocationName = useSelector((state: RootState) => state.appData?.selectedLocation?.displayText);
  const currentYear = new Date().getFullYear();
  const kcphMetrics = useSelector((state: RootState) => state.dlo.dashboard.kcphData) || {
    forecastedKcph: null,
    targetKcph: null,
    scheduledKcph: null,
    actualKcph: null,
    estimatedKcph: null,
    adjustedRecommendedKcph: null,
    crewedKcph: null,
  };
  const kcphLoading = useSelector((state: RootState) => state.dlo.dashboard.kcphLoading);
  const kcphError = useSelector((state: RootState) => state.dlo.dashboard.kcphError);
  const scheduleData = useSelector((state: RootState) => state.dlo.dashboard.scheduleData);
  const appDispatch = useAppDispatch();



  // Fetch KCPH metrics when location, period, week, year are available
  useEffect(() => {
    if (!selectedLocationId || !createSchedulePeriod || !createScheduleWeek || !createScheduleYear) return;
    appDispatch(fetchKCPHData({
      period: createSchedulePeriod,
      week: fromBackendWeek(createScheduleWeek).week,
      year: createScheduleYear
    }));
  }, [selectedLocationId, createSchedulePeriod, createScheduleWeek, createScheduleYear, appDispatch]);

  // Acquire session lock as soon as the composite key is known so the
  // expiration warning timer is armed even before the user saves.
  // (LOCK_CONFIGS only matches POST/PUT/DELETE; this page initially fires only
  // GETs, so without an explicit acquire no warning would ever fire.)
  useEffect(() => {
    if (!selectedLocationId || !createSchedulePeriod || !createScheduleWeek || !createScheduleYear) return;

    const lockService = getSessionLockService();
    if (!lockService) return;

    const lockParams = {
      module: 'DLO',
      submodule: 'Schedule',
      locId: String(selectedLocationId),
      period: createSchedulePeriod,
      week: createScheduleWeek,
      year: createScheduleYear,
      sessionId: '',
    };

    // Skip if we already hold a lock for this exact resource (e.g. user came
    // here from Schedule Settings which acquired it via cleanScheduleSettings).
    if (lockService.getExistingLockByParams(lockParams)) {
      console.log('[DLOCreateSchedule] Lock already active for this schedule, skipping acquire');
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const lockResponse = await lockService.acquireLock(lockParams);
        if (cancelled) return;
        console.log('[DLOCreateSchedule] Lock acquired on mount:', {
          resourceId: lockResponse?.resourceId,
          expiresAt: lockResponse?.expiresAt,
        });
      } catch (error: any) {
        if (cancelled) return;
        if (error?.response?.status === 423 && error.lockConflict) {
          const conflictData = error.lockConflict;
          appDispatch(
            showSessionLockConflictModal({
              isLocked: conflictData.isLocked,
              alreadyLocked: conflictData.alreadyLocked,
              lockedBy: conflictData.lockedBy,
              lockedByName: conflictData.lockedByName,
              lockedAt: conflictData.lockedAt,
              expiresAt: conflictData.expiresAt,
              resourceType: 'Schedule',
            }),
          );
        } else {
          console.warn('[DLOCreateSchedule] Lock acquire failed on mount:', error);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedLocationId, createSchedulePeriod, createScheduleWeek, createScheduleYear, appDispatch]);

  // Convert calculated week back to business week using formula: week = ((calculatedWeek - 1) % 4) + 1
  const getBusinessWeek = (calculatedWeek: number): number => {
    if (!calculatedWeek || calculatedWeek <= 0) return 0;
    return ((calculatedWeek - 1) % 4) + 1;
  };
  
  const targetWeek = {
    period: createSchedulePeriod || 0,
    week: createScheduleWeek ? getBusinessWeek(createScheduleWeek) : 0,
    year: createScheduleYear || 0
  };
  
  const currentWeek = {
    period: appData?.periodWeek?.data?.period || 0,
    week: appData?.periodWeek?.data?.week || 0,
    year: currentYear
  };

  // Tab state hook with intelligent default based on week comparison
  const { tabState, setActiveTab } = useTabState(targetWeek, currentWeek);

  // Fetch appropriate hours data based on active tab
  useEffect(() => {
    if (!createSchedulePeriod || !createScheduleWeek) return;
    
    if (tabState.activeTab === 'schedule') {
      console.log('Fetching Schedule Hours data for schedule tab');
      dispatch(fetchCreateScheduleHours());
    } else if (tabState.activeTab === 'actual') {
      console.log('Fetching Actual Hours data for actual tab');
      dispatch(fetchActualHours());
    }
  }, [tabState.activeTab, createSchedulePeriod, createScheduleWeek, dispatch]);

  // Notification state
  const [notification, setNotification] = useState<{
    visible: boolean;
    message: string;
    variant: NotificationVariant;
  }>({
    visible: false,
    message: '',
    variant: 'primary'
  });

  // Function to show notification
  const showNotification = (message: string, variant: NotificationVariant = 'primary') => {
    setNotification({
      visible: true,
      message,
      variant
    });
    
    // Auto-hide notification after 5 seconds
    setTimeout(() => {
      setNotification(prev => ({ ...prev, visible: false }));
    }, 5000);
  };

  // Reset Create Schedule state when component unmounts (user leaves the page)
  useEffect(() => {
    console.log('DLOCreateSchedule component mounted - ready to load fresh data');
    
    // Cleanup function - runs when component unmounts (user navigates away)
    return () => {
      console.log('DLOCreateSchedule component unmounting - resetting all state for fresh data on next visit');
      resetCreateScheduleState();
    };
  }, [resetCreateScheduleState]);

  // Navigation-aware data refresh system using state tracking
  const navigationKey = useRef<string>('');
  
  useEffect(() => {
    const currentPeriodWeek = {
      period: createSchedulePeriod || appData?.periodWeek?.data?.period || 0,
      week: createScheduleWeek || appData?.periodWeek?.data?.week || 0
    };
    
    const currentKey = `${currentPeriodWeek.period}-${currentPeriodWeek.week}`;
    
    // Check if this is initial load or if period/week has changed
    if (navigationKey.current && navigationKey.current !== currentKey) {
      console.log(`[Navigation] Period/Week changed from ${navigationKey.current} to ${currentKey} - forcing complete data refresh`);
      console.log(`[Navigation] Current employee rows count:`, employeeRows?.length || 0);
      console.log(`[Navigation] Current schedule employee data:`, scheduleEmployeeData ? 'exists' : 'null');
      
      // Force a complete state reset and data refresh
      resetCreateScheduleState();
      
      // Add a delay to ensure state is cleared before new data loads, then force refresh
      setTimeout(() => {
        console.log(`[Navigation] State reset complete, forcing API refetch for ${currentKey}`);
        // The useEffect in useCreateSchedule will automatically trigger data fetching
        // when the period/week dependencies change
      }, 50);
    }
    
    // Update the navigation key for future comparisons
    navigationKey.current = currentKey;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createSchedulePeriod, createScheduleWeek, appData?.periodWeek?.data?.period, appData?.periodWeek?.data?.week, resetCreateScheduleState]);

  // Transform data using utilities.
  // Memoized so that Picker dropdowns downstream don't receive a new array reference
  // on every render (which would cause react-native-dropdown-picker to call setState
  // during rendering and trigger React error #185 "Too many re-renders").
  const employeeOptions = React.useMemo(() => getEmployeeOptions(employees), [employees]);
  const roleOptions = React.useMemo(() => getRoleOptions(employeeRoles), [employeeRoles]);
  const shiftOptions = React.useMemo(() => getShiftOptions(shiftTimes), [shiftTimes]);

  // Get transformed data for the table - memoized to prevent unnecessary recalculations
  const tableData = React.useMemo(() => 
    scheduleHours ? transformApiDataToTableFormat(scheduleHours) : [],
    [scheduleHours]
  );

  // Get transformed data for actual hours table (EST vs ACTUAL)
  // Uses actualHours data from separate API call with isActual=true
  const actualTableData = React.useMemo(() => 
    actualHours ? transformActualHoursToTableFormat(actualHours) : [],
    [actualHours]
  );
  
  const rolesByType = React.useMemo(() => 
    scheduleHours ? extractRolesByType(scheduleHours, tableData) : { DSD: [], 'NON-DSD': [], Retail: [], Other: [] },
    [scheduleHours, tableData]
  );
  
  console.log('Extracted roles by type:', rolesByType);
  const mergedRolesWithFilterState = React.useMemo(
    () => getMergedRolesWithFilterState(rolesByType, filteredRoles),
    [rolesByType, filteredRoles]
  );
  const hasData = tableData.length > 0;

  // Track when filtered roles have been initialized for current schedule data
  const initializedScheduleKey = useRef<string>('');
  
  // Initialize filtered roles with zero-variance roles disabled when schedule hours are first loaded
  useEffect(() => {
    // Create a unique key based on the actual rolesByType data structure (serialize all role names)
    const roleSignature = Object.entries(rolesByType)
      .map(([category, roles]) => `${category}:${roles.map((r: any) => `${r.name}-${r.enabled}`).join(',')}`)
      .join('|');
    const currentScheduleKey = `${createSchedulePeriod}-${createScheduleWeek}-${roleSignature}`;
    
    // Only initialize if:
    // 1. We have schedule hours and data
    // 2. The rolesByType data has actually changed (different signature)
    if (scheduleHours && hasData && rolesByType && initializedScheduleKey.current !== currentScheduleKey) {
      console.log('=== FILTER INITIALIZATION TRIGGERED ===');
      console.log('Previous key:', initializedScheduleKey.current);
      console.log('New key:', currentScheduleKey);
      console.log('Initializing filtered roles with zero-variance roles disabled:', JSON.stringify(rolesByType, null, 2));
      dispatch(setFilteredRoles(rolesByType));
      dispatch(setActualFilteredRoles(rolesByType));
      initializedScheduleKey.current = currentScheduleKey;
      console.log('=== FILTER INITIALIZATION COMPLETE ===');
    } else {
      console.log('Skipping initialization - keys match or missing data:', {
        hasScheduleHours: !!scheduleHours,
        hasData,
        hasRolesByType: !!rolesByType,
        currentKey: currentScheduleKey,
        previousKey: initializedScheduleKey.current,
        keysMatch: initializedScheduleKey.current === currentScheduleKey
      });
    }
  }, [scheduleHours, hasData, rolesByType, dispatch, createSchedulePeriod, createScheduleWeek]);

  // Initialize handlers hook with all required props
  const handlers = useCreateScheduleHandlers({
    createScheduleData,
    employeeRows,
    deletedEmployeeRows: createScheduleData.deletedEmployeeRows,
    actualEmployeeRows,
    actualDeletedEmployeeRows: createScheduleData.actualDeletedEmployeeRows,
    selectedDays,
    actualSelectedDays,
    tableData,
    tabState,
    showNotification,
    setModalState,
    createSchedulePeriod,
    createScheduleWeek,
    createScheduleYear,
    appData,
    onNavigate,
    setActiveTab,
    targetWeek,
    currentWeek,
    hasUnsavedChanges,
    scheduleEmployeeData,
    initialSchedules,
    employees,
    shiftTimes,
    onCloseAllDropdowns: handleCloseAllDropdowns
  });

  // Format period week year display
  const periodWeekYearDisplay = getPeriodWeekYearDisplay(createSchedulePeriod, createScheduleWeek, createScheduleYear);

  // Week cutoff logic using device local time - derive everything from week comparison
  const weekComparison = compareWeek(targetWeek, currentWeek);
  
  // Debug logging for week comparison
  console.log('Week comparison:', {
    targetWeek: `P${targetWeek.period} W${targetWeek.week}`,
    currentWeek: `P${currentWeek.period} W${currentWeek.week}`,
    calculatedWeek: createScheduleWeek,
    comparison: weekComparison
  });
  
  // Derive week types from comparison only (no status field needed)
  const isPastWeek = weekComparison < 0;
  const isCurrentWeek = weekComparison === 0;
  const isFutureWeek = weekComparison > 0;

  // Truth Table Implementation:
  // Past Week: Scheduled ❌, Actual ❌
  // Current Week: Scheduled ❌, Actual ✅  
  // Future Week: Scheduled ✅, Actual ❌
  let scheduledEditable = false;
  let actualEditable = false;

  if (isPastWeek) {
    scheduledEditable = false;  // Past: Schedule disabled
    actualEditable = false;     // Past: Actual disabled
  } else if (isCurrentWeek) {
    scheduledEditable = false;  // Current: Schedule disabled  
    actualEditable = true;      // Current: Actual enabled
  } else if (isFutureWeek) {
    scheduledEditable = true;   // Future: Schedule enabled
    actualEditable = false;     // Future: Actual disabled
  }

  console.log('Truth table applied:', {
    weekType: isPastWeek ? 'Past' : isCurrentWeek ? 'Current' : 'Future',
    scheduledEditable,
    actualEditable
  });

  // Input field specific flags per truth table:
  // Past: Both inputs read-only
  // Current: Scheduled read-only, Actual editable
  // Future: Scheduled editable, Actual read-only
  const scheduledInputsEnabled = scheduledEditable;
  const actualInputsEnabled = actualEditable;

  // Day filter control flags per truth table:
  // Past: Both day filters disabled
  // Current: Scheduled day filters disabled, Actual day filters enabled
  // Future: Scheduled day filters enabled, Actual day filters disabled
  const scheduledDayFiltersEnabled = scheduledEditable;
  const actualDayFiltersEnabled = actualEditable;

  // Handle save operation success/error states
  useEffect(() => {
    if (saveSuccess) {
      setModalState.setSuccessModal(true, 'Progress Saved successfully');
      setRequiresSave(false);
    }
  }, [saveSuccess]);

  useEffect(() => {
    if (hasUnsavedChanges) {
      setRequiresSave(true);
    }
  }, [hasUnsavedChanges]);

  // Note: saveError is handled directly in the save handler (useCreateScheduleHandlers)
  // to prevent get API calls on failure and keep page state unchanged

  // Handler for print button - opens export modal first
  const handlePrint = () => {
    setExportModalVisible(true);
  };

  // Handler for File 1 - Print with hours displayed
  const handlePrintWithHours = () => {
    setHideHours(false);
    setExportModalVisible(false);
    setPrintModalVisible(true);
  };

  // Handler for File 2 - Print with hours hidden
  const handlePrintWithoutHours = () => {
    setHideHours(true);
    setExportModalVisible(false);
    setPrintModalVisible(true);
  };

  // Check if any employee row (across both schedule and actual tabs) has at least one day with hours > 0
  const hasAnyEmployeeWithHours = React.useMemo(() => {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const hasHours = (rows: any[]) =>
      rows && rows.some((row: any) => days.some((day: string) => parseFloat(row[day] || '0') > 0));
    return hasHours(employeeRows) || hasHours(actualEmployeeRows);
  }, [employeeRows, actualEmployeeRows]);

  // Filter schedule details based on current period/week
  const currentScheduleDetails = React.useMemo(() => {
    if (!scheduleData?.data || !createSchedulePeriod || !createScheduleWeek) {
      return null;
    }

    // Filter schedules to find the matching one
    const matchingSchedule = scheduleData.data.find(
      (schedule: any) =>
        schedule.PRD === createSchedulePeriod &&
        schedule.WK === createScheduleWeek &&
        schedule['Loc ID'] === selectedLocationId
    );

    return matchingSchedule || null;
  }, [scheduleData, createSchedulePeriod, createScheduleWeek, selectedLocationId]);

  return (
    <ScrollView style={styles.container}>
      {/* Breadcrumb Navigation with Period/Week/Year */}
      <View style={styles.breadcrumbContainer}>
        <DLOLaborOptimizationBreadcrumb
          currentPage="Create Schedule"
          periodWeekYear={periodWeekYearDisplay}
          onHomePress={handlers.handleHomePress}
          onLaborOptimizationPress={handlers.handleLaborOptimizationPress}
        />
      </View>

      {/* Notification */}
      {notification.visible && (
        <DLONotification
          message={notification.message}
          variant={notification.variant}
          visible={notification.visible}
          onDismiss={() => setNotification(prev => ({ ...prev, visible: false }))}
        />
      )}

      {/* Page Header with KCPH Metrics and Action Buttons */}
      <DLOCreateScheduleHeader
        forecastedKCPH={Math.round(kcphMetrics.forecastedKcph ?? 0)}
        targetKCPH={Math.round(kcphMetrics.targetKcph ?? 0)}
        scheduledKCPH={Math.round(kcphMetrics.scheduledKcph ?? 0)}
        estimatedKCPH={Math.round(kcphMetrics.estimatedKcph ?? 0)}
        actualKCPH={Math.round(kcphMetrics.actualKcph ?? 0)}
        adjustedRecommendedKCPH={Math.round(kcphMetrics.adjustedRecommendedKcph ?? 0)}
        crewedKCPH={Math.round(kcphMetrics.crewedKcph ?? 0)}
        activeTab={tabState.activeTab}
        onScheduleSettings={handlers.handleScheduleSettings}
        onPrint={handlePrint}
        onSave={handlers.handleUniversalSave}
        onSubmit={handlers.handleSubmit}
        onNavigate={onNavigate}
        isSubmitDisabled={
          scheduleEmployeeDataLoading || 
          submitLoading ||
          !hasAnyEmployeeWithHours ||
          requiresSave ||
          (tabState.activeTab === 'schedule' ? !scheduledEditable : !actualEditable)
        }
        isSubmitLoading={submitLoading}
        isScheduleSettingsDisabled={false} // Keep Schedule Settings enabled during loading
        isPrintDisabled={!employeeRows || employeeRows.length === 0}
        isSaveDisabled={
          saveLoading || 
          scheduleEmployeeDataLoading || 
          !hasUnsavedChanges ||
          (tabState.activeTab === 'schedule' && !scheduledEditable) ||
          (tabState.activeTab === 'actual' && !actualEditable) ||
          hasIncompleteEmployeeRows(tabState.activeTab === 'schedule' ? employeeRows : actualEmployeeRows)
        }
        isSaveLoading={saveLoading}
        hasUnsavedChanges={hasUnsavedChanges}
      />

      {/* Content Area */}
      <View style={styles.content}>
        <DLOCreateScheduleTable
          tableData={tableData}
          actualTableData={actualTableData}
          employeeRows={employeeRows}
          actualEmployeeRows={actualEmployeeRows}
          employeeOptions={employeeOptions}
          roleOptions={roleOptions}
          shiftOptions={shiftOptions}
          mergedRolesWithFilterState={mergedRolesWithFilterState}
          selectedDays={selectedDays}
          actualSelectedDays={actualSelectedDays}
          dayFilter={dayFilter}
          actualDayFilter={actualDayFilter}
          filteredRoles={filteredRoles}
          actualFilteredRoles={actualFilteredRoles}
          scheduleHoursLoading={scheduleHoursLoading}
          scheduleEmployeeDataLoading={scheduleEmployeeDataLoading}
          employeesLoading={false} // TODO: Get from hook
          employeeRolesLoading={false} // TODO: Get from hook
          shiftTimesLoading={false} // TODO: Get from hook
          activeTab={tabState.activeTab}
          hasData={hasData}
          canSplitRole={calculateCanSplitRole(tabState, employeeRows, actualEmployeeRows)}
          scheduledEditable={scheduledEditable}
          actualEditable={actualEditable}
          scheduledInputsEnabled={scheduledInputsEnabled}
          actualInputsEnabled={actualInputsEnabled}
          scheduledDayFiltersEnabled={scheduledDayFiltersEnabled}
          actualDayFiltersEnabled={actualDayFiltersEnabled}
          isDropdownOpen={isDropdownOpen}
          handleGlobalDropdownOpen={handleGlobalDropdownOpen}
          handleAddEmployeeRow={handlers.handleAddEmployeeRow}
          handleAddActualEmployeeRow={handlers.handleAddEmployeeRow}
          handleTabToggle={handlers.handleTabToggle}
          handleEmployeeRowUpdate={handlers.handleEmployeeRowUpdate}
          handleActualEmployeeRowUpdate={handlers.handleActualEmployeeRowUpdate}
          handleEmployeeRowDelete={handlers.handleEmployeeRowDelete}
          handleActualEmployeeRowDelete={handlers.handleActualEmployeeRowDelete}
          handleSplitRowUpdate={handlers.handleEmployeeRowUpdate}
          handleSplitRole={tabState.activeTab === 'actual' ? handlers.handleActualSplitRole : handlers.handleSplitRole}
          handleActualSplitRole={handlers.handleActualSplitRole}
          handleDeleteSelectedRows={handlers.handleDeleteSelectedRows}
          handleDeleteSelectedActualRows={handlers.handleDeleteSelectedActualRows}
          handleSplitRoleDelete={handlers.handleSplitRoleDelete}
          handleActualSplitRoleDelete={handlers.handleActualSplitRoleDelete}
          handleMassUpdate={handlers.handleMassUpdate}
          handleActualMassUpdate={handlers.handleActualMassUpdate}
          handleSelectAllEmployees={handlers.handleSelectAllEmployees}
          handleSelectAllActualEmployees={handlers.handleSelectAllActualEmployees}
          handleRoleFilterSave={handlers.handleRoleFilterSave}
          handleActualRoleFilterSave={handlers.handleActualRoleFilterSave}
          handleDayFilterSave={handlers.handleDayFilterSave}
          handleActualDayFilterSave={handlers.handleActualDayFilterSave}
          handleDayFilterPress={handlers.handleDayFilterPress}
          handleActualDayFilterPress={handlers.handleActualDayFilterPress}
          handleEmployeeRowUpdateMultiple={(updates) => {
            updates.forEach(({ id, updatedData }) => {
              createScheduleData.dispatch(updateEmployeeRow({ id, updates: updatedData }));
            });
          }}
          handleActualEmployeeRowUpdateMultiple={(updates) => {
            updates.forEach(({ id, updatedData }) => {
              createScheduleData.dispatch(updateActualEmployeeRow({ id, updates: updatedData }));
            });
          }}
          handleUniversalSave={handlers.handleUniversalSave}
          saveLoading={saveLoading}
          hasUnsavedChanges={hasUnsavedChanges}
        />
      </View>

      {/* All Modals */}
            {/* Line 437 omitted */}
      <DLOCreateScheduleModals
        modalState={modalState}
        setModalState={setModalState}
        onResetSaveState={() => dispatch(resetSaveState())}
        onPerformSubmission={handlers.performSubmission}
        onProceedWithUnsavedChanges={handlers.proceedWithUnsavedChanges}
      />

      {/* Export Modal */}
      <DLOExportModal
        visible={exportModalVisible}
        onClose={() => setExportModalVisible(false)}
        onPrintWithHours={handlePrintWithHours}
        onPrintWithoutHours={handlePrintWithoutHours}
      />

      {/* Print Modal */}
      <DLOPrintModal
        visible={printModalVisible}
        onClose={() => setPrintModalVisible(false)}
        scheduleDetails={currentScheduleDetails}
        employeeRows={tabState.activeTab === 'schedule' ? employeeRows : actualEmployeeRows}
        locationName={selectedLocationName}
        periodWeekYear={periodWeekYearDisplay}
        activeTab={tabState.activeTab}
        hideHours={hideHours}
      />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  breadcrumbContainer: {
    backgroundColor: 'rgb(240, 245, 252)',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
  },
  content: {
    flex: 1,
    backgroundColor: 'rgb(240, 245, 252)',
    marginBottom: 20,
    paddingVertical: 20,
  },
});
